import { useCallback, useSyncExternalStore } from 'react'

type TEntry = {
    list: MediaQueryList
    subscribers: Set<() => void>
}

const entries = new Map<string, TEntry>()

const getEntry = (query: string): TEntry => {
    const existing = entries.get(query)

    if (existing) {
        return existing
    }

    const entry: TEntry = { list: window.matchMedia(query), subscribers: new Set() }

    entry.list.addEventListener('change', () => {
        entry.subscribers.forEach(notify => notify())
    })
    entries.set(query, entry)

    return entry
}

/**
 * For components rendered many times on one page: one `MediaQueryList` per query instead of one per
 * instance. Under Preact it reports the real match on the first client render, hydration included —
 * markup the server also prints has to wait for `useIsHydrating`, as `useIsTabletUp` does.
 */
export const useSharedMediaQuery = (query: string): boolean => {
    const subscribe = useCallback(
        (onStoreChange: () => void) => {
            const entry = getEntry(query)

            entry.subscribers.add(onStoreChange)

            return () => {
                entry.subscribers.delete(onStoreChange)
            }
        },
        [query],
    )

    const getSnapshot = useCallback(() => getEntry(query).list.matches, [query])

    return useSyncExternalStore(subscribe, getSnapshot, () => false)
}
